import { useState, useEffect } from 'preact/hooks'
import { useForm } from 'react-hook-form'
import { X, Package, Upload, AlertTriangle, Info } from 'lucide-preact'
import { assetApi, type WebAssetCreateDto } from '@/api/asset'
import type { WebsterAssetType } from '@/hooks/useWebsterBuilder'
import { setFormValidationErrors } from '@/utils/errorHandling'
import { useLookups } from '@/hooks/useLookups'
import { useEmailTypesLookup } from '@/hooks/useEmailTypesLookup'
import { TextBoxField, Select, FileUpload, FormActions } from '@/components'
import { BRAND_CLASSES } from '@/constants/theme'
import { buildAcceptString } from '../constants/fileRestrictions'
import { effectiveSettingsState } from '@/state/workflowSettings'
import { GoogleDrivePicker } from '@/components/common/GoogleDrivePicker'
import { OneDrivePicker } from '@/components/common/OneDrivePicker'
import { DropboxChooser } from '@/components/common/DropboxChooser'
import type { GoogleDriveFile } from '@/components/common/GoogleDrivePicker'
import type { OneDriveFile } from '@/components/common/OneDrivePicker'
import type { DropboxFile } from '@/components/common/DropboxChooser'
import { GoogleDriveIcon } from '@/components/common/icons/GoogleDriveIcon'
import { OneDriveIcon } from '@/components/common/icons/OneDriveIcon'
import { DropboxIcon } from '@/components/common/icons/DropboxIcon'
import { useGoogleDriveAuth } from '@/hooks/useGoogleDriveAuth'
import { useOneDriveAuth } from '@/hooks/useOneDriveAuth'
import { useDropboxAuth } from '@/hooks/useDropboxAuth'
import { isCloudFileOverLimit, cloudFileSizeLabel, cloudMaxSizeLabel } from './cloudUploadSize'

type UploadMethod = 'file' | 'googledrive' | 'onedrive' | 'dropbox'

const DIRECT_UPLOAD_MAX_MB = 100
const LARGE_FILE_WARNING_MB = 50
const NAME_MAX_LENGTH = 255

interface CreateAssetModalProps {
  isOpen: boolean
  onClose: () => void
  clientCompanyId: string
  workspaceId: string
  campaignId: string
  webId: string
  // ORB-481: the web's type description ("Online Event" / "Download" / "Meeting" / "Demo"), used only
  // to name the web type when explaining that it has no email-type catalog.
  webType: string
  // Webster types (Landing Page / Email Template / Email Copy) pass back the
  // created asset so the parent can open its builder; file-upload types omit it.
  onAssetCreated: (created?: { id: string; type: WebsterAssetType }) => void
  existingAssetTypes?: string[]
}

interface AssetFormData {
  Name: string
  Type: string
  Status: string
  // ORB-363: chosen catalog email type for an Email Copy. '' = Custom (blank copy, today's behavior).
  EmailTypeKey: string
}

export function CreateAssetModal({ isOpen, onClose, clientCompanyId, workspaceId, campaignId, webId, webType, onAssetCreated, existingAssetTypes }: CreateAssetModalProps) {
  const [uploadMethod, setUploadMethod] = useState<UploadMethod>('file')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [fileError, setFileError] = useState<string | undefined>()
  const [pickedDriveFile, setPickedDriveFile] = useState<GoogleDriveFile | null>(null)
  const [driveError, setDriveError] = useState<string | undefined>()
  const [pickedOneDriveFile, setPickedOneDriveFile] = useState<OneDriveFile | null>(null)
  const [oneDriveError, setOneDriveError] = useState<string | undefined>()
  const [pickedDropboxFile, setPickedDropboxFile] = useState<DropboxFile | null>(null)
  const [dropboxError, setDropboxError] = useState<string | undefined>()
  const googleDriveAuth = useGoogleDriveAuth(isOpen)
  const oneDriveAuth = useOneDriveAuth(isOpen)
  const dropboxAuth = useDropboxAuth(isOpen)
  const { getAssetTypes, getAssetStatuses } = useLookups()

  // Read effective workflow settings from signal (loaded by WebAssetsTab)
  const { settings: effectiveSettings, loading: loadingSettings } = effectiveSettingsState.value
  const fileRestrictions = effectiveSettings?.FileRestrictions ?? null

  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isValid },
    reset,
    setError,
    setValue,
    watch,
  } = useForm<AssetFormData>({
    mode: 'onChange',
    defaultValues: {
      Name: '',
      Type: '',
      Status: '',
      EmailTypeKey: ''
    }
  })

  // Pre-populate default status from effective settings when modal opens
  // Include assetStatuses so this re-runs if lookups load after the modal opens
  const assetStatuses = getAssetStatuses()
  useEffect(() => {
    if (!isOpen || !effectiveSettings?.DefaultStatusOnUpload) return
    const match = assetStatuses.find(s => s.Description === effectiveSettings.DefaultStatusOnUpload)
    if (match) setValue('Status', match.Key)
  }, [isOpen, effectiveSettings?.DefaultStatusOnUpload, assetStatuses, setValue])

  // ESC key to close
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !isSubmitting) handleClose()
    }
    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      return () => document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen, isSubmitting])

  // Derive file upload constraints from workflow settings
  const acceptString = fileRestrictions ? buildAcceptString(fileRestrictions) : null
  // The direct tab's ceiling is the Azure Functions request body limit, nothing else. The per-web
  // FileRestrictions size is no longer editable and was never enforced, so letting a stale stored
  // value narrow this would cap uploads with no way to change it (ORB-721).
  const maxSizeMB = DIRECT_UPLOAD_MAX_MB
  const hasSettings = fileRestrictions !== null
  const hasAllowedFileTypes = acceptString !== null


  // Cloud tabs: the ceiling comes from the API, never a constant here. A hardcoded frontend number
  // drifting from what the server enforces is exactly what produced this ticket (ORB-721).
  const maxUploadFileSizeBytes = effectiveSettings?.MaxUploadFileSizeBytes ?? null
  const pickedCloudFileSizeBytes =
    uploadMethod === 'googledrive' ? pickedDriveFile?.sizeBytes ?? null
      : uploadMethod === 'onedrive' ? pickedOneDriveFile?.sizeBytes ?? null
        : uploadMethod === 'dropbox' ? pickedDropboxFile?.bytes ?? null
          : null
  const hasPickedCloudFile =
    (uploadMethod === 'googledrive' && pickedDriveFile !== null)
    || (uploadMethod === 'onedrive' && pickedOneDriveFile !== null)
    || (uploadMethod === 'dropbox' && pickedDropboxFile !== null)
  const isCloudFileTooLarge = isCloudFileOverLimit(pickedCloudFileSizeBytes, maxUploadFileSizeBytes)
  const pickedCloudSizeText = cloudFileSizeLabel(pickedCloudFileSizeBytes)
  const cloudMaxSizeText = cloudMaxSizeLabel(maxUploadFileSizeBytes)

  // File size warnings
  const fileSizeMB = selectedFile ? selectedFile.size / 1048576 : 0
  const isFileOverLimit = selectedFile !== null && fileSizeMB > DIRECT_UPLOAD_MAX_MB
  const isFileLarge = selectedFile !== null && fileSizeMB > LARGE_FILE_WARNING_MB && !isFileOverLimit

  const assetTypes = getAssetTypes()
  const hasLandingPage = existingAssetTypes?.includes('Landing Page') ?? false
  const typeOptions = assetTypes
    .filter(t => !(t.Description === 'Landing Page' && hasLandingPage))
    .map(t => ({ value: t.Key, label: t.Description }))
  const statusOptions = assetStatuses.map(s => ({ value: s.Key, label: s.Description }))

  // Webster-powered types skip file upload — Webster generates content
  const selectedType = watch('Type')
  const selectedTypeLabel = typeOptions.find(t => t.value === selectedType)?.label
  const isWebsterType = selectedTypeLabel === 'Email Template' || selectedTypeLabel === 'Email Copy' || selectedTypeLabel === 'Landing Page'

  // ORB-363: the web's email-type catalog drives the picker shown when creating an Email Copy. Only
  // fetched when the modal's open and the type is Email Copy, to avoid a needless request otherwise.
  const wantEmailTypes = isOpen && selectedTypeLabel === 'Email Copy'
  const emailTypes = useEmailTypesLookup(wantEmailTypes ? workspaceId : undefined, wantEmailTypes ? webId : undefined)
  const emailTypeOptions = [
    { value: '', label: 'Custom (blank — write it yourself)' },
    ...(emailTypes.data ?? []).map(t => ({
      value: t.Key,
      label: t.AlreadyGenerated ? `${t.Name} (already added)` : t.Name,
    })),
  ]

  // ORB-481: the three catalog outcomes are visually distinct. Previously all of them rendered as
  // "no dropdown", so a web type with no catalog (Meeting/Demo) was indistinguishable from a lookup
  // that failed — which cost real debugging time when a picker was reported missing and the request
  // had in fact returned an empty array by design.
  const hasEmailTypeCatalog = (emailTypes.data?.length ?? 0) > 0
  const emailTypeCatalogEmpty = emailTypes.data !== null && emailTypes.data.length === 0
  const showEmailTypeSection = selectedTypeLabel === 'Email Copy'

  const onSubmit = async (data: AssetFormData) => {
    // Symmetric with the direct-file branch's own isFileOverLimit guard below. The disabled button
    // already covers this, but a submit path that trusts only the button is one refactor away from
    // not being guarded at all. False unless a cloud tab has a file we know to be oversized.
    //
    // Scoped by !isWebsterType to match how isSaveDisabled scopes it. Switching the type to a
    // Webster one hides the cloud tabs without clearing the picked file, so an unscoped guard would
    // leave a stale oversized pick silently killing a Create that uploads nothing at all.
    if (!isWebsterType && isCloudFileTooLarge) return

    // Webster types: create asset without file — Webster generates content
    if (isWebsterType) {
      setIsSubmitting(true)
      try {
        // ORB-363: Email Copy + a chosen catalog type → generate that type (drafted by Webster from the
        // catalog guidance). Custom (or any other Webster type) → create a blank asset and open the builder.
        //
        // ORB-481: gated on the picker being VISIBLE, not just on the value being set. react-hook-form
        // keeps unmounted field values (shouldUnregister defaults to false), so if the catalog ever went
        // from present to error/empty after a selection, a stale EmailTypeKey the user can no longer see
        // would silently drive generation. Reading the key only when its input is on screen makes that
        // structurally impossible rather than relying on the reset in handleClose.
        if (selectedTypeLabel === 'Email Copy' && hasEmailTypeCatalog && data.EmailTypeKey) {
          const genResponse = await assetApi.generateEmailAsset(
            clientCompanyId, workspaceId, campaignId, webId, data.EmailTypeKey)
          if (genResponse.error) {
            if (genResponse.status === 422) {
              setFormValidationErrors(genResponse, setError)
            }
            return
          }
          onAssetCreated(genResponse.data?.AssetId
            ? { id: genResponse.data.AssetId, type: 'Email Copy' as WebsterAssetType }
            : undefined)
          emailTypes.refetch() // refresh AlreadyGenerated so a reopen shows this type as already added
          handleClose()
          return
        }

        const createDto: WebAssetCreateDto = {
          Name: data.Name.trim(),
          Type: data.Type,
          Status: data.Status || undefined
        }

        const response = await assetApi.createWebAssetWithoutFile(clientCompanyId, workspaceId, campaignId, webId, createDto)

        if (response.error) {
          if (response.status === 422) {
            setFormValidationErrors(response, setError)
          }
          return
        }

        // Hand the new asset back so the parent drops the user straight into the
        // Webster builder (mirrors strict-mode quick-create). selectedTypeLabel is
        // guaranteed to be a Webster type here since isWebsterType is true.
        const created = response.data as { Id?: string } | null
        onAssetCreated(created?.Id ? { id: created.Id, type: selectedTypeLabel as WebsterAssetType } : undefined)
        handleClose()
      } catch (error) {
        console.error('Error creating email asset:', error)
      } finally {
        setIsSubmitting(false)
      }
      return
    }

    if (uploadMethod === 'file') {
      if (!selectedFile) {
        setFileError('Please select a file to upload')
        return
      }
      if (isFileOverLimit) return

      setIsSubmitting(true)
      try {
        const createDto: WebAssetCreateDto = {
          Name: data.Name.trim(),
          Type: data.Type,
          Status: data.Status || undefined
        }

        const response = await assetApi.createWebAsset(clientCompanyId, workspaceId, campaignId, webId, createDto, selectedFile)

        if (response.error) {
          if (response.status === 422) {
            setFormValidationErrors(response, setError)
          }
          return
        }

        onAssetCreated()
        handleClose()
      } catch (error) {
        console.error('Error creating asset:', error)
      } finally {
        setIsSubmitting(false)
      }
    } else if (uploadMethod === 'googledrive') {
      if (!pickedDriveFile) return

      setIsSubmitting(true)
      try {
        const createDto: WebAssetCreateDto = {
          Name: data.Name.trim(),
          Type: data.Type,
          Status: data.Status || undefined,
          CloudStorageFileId: pickedDriveFile.fileId,
          CloudStorageFileName: pickedDriveFile.fileName,
          CloudStorageMimeType: pickedDriveFile.mimeType,
          CloudStorageProvider: 'GoogleDrive'
        }

        const response = await assetApi.createWebAssetByCloudStorage(clientCompanyId, workspaceId, campaignId, webId, createDto)

        if (response.error) {
          if (response.status === 422) {
            setFormValidationErrors(response, setError)
          }
          return
        }

        onAssetCreated()
        handleClose()
      } catch (error) {
        console.error('Error creating asset:', error)
      } finally {
        setIsSubmitting(false)
      }
    } else if (uploadMethod === 'onedrive') {
      if (!pickedOneDriveFile) return

      setIsSubmitting(true)
      try {
        const createDto: WebAssetCreateDto = {
          Name: data.Name.trim(),
          Type: data.Type,
          Status: data.Status || undefined,
          CloudStorageFileId: pickedOneDriveFile.fileId,
          CloudStorageFileName: pickedOneDriveFile.fileName,
          CloudStorageMimeType: pickedOneDriveFile.mimeType,
          CloudStorageProvider: 'OneDrive'
        }

        const response = await assetApi.createWebAssetByCloudStorage(clientCompanyId, workspaceId, campaignId, webId, createDto)

        if (response.error) {
          if (response.status === 422) {
            setFormValidationErrors(response, setError)
          }
          return
        }

        onAssetCreated()
        handleClose()
      } catch (error) {
        console.error('Error creating asset:', error)
      } finally {
        setIsSubmitting(false)
      }
    } else if (uploadMethod === 'dropbox') {
      if (!pickedDropboxFile) return

      setIsSubmitting(true)
      try {
        const createDto: WebAssetCreateDto = {
          Name: data.Name.trim(),
          Type: data.Type,
          Status: data.Status || undefined,
          CloudStorageFileId: pickedDropboxFile.fileId,
          CloudStorageFileName: pickedDropboxFile.fileName,
          CloudStorageMimeType: pickedDropboxFile.mimeType,
          CloudStorageProvider: 'Dropbox',
          DirectDownloadUrl: pickedDropboxFile.directDownloadUrl
        }

        const response = await assetApi.createWebAssetByCloudStorage(clientCompanyId, workspaceId, campaignId, webId, createDto)

        if (response.error) {
          if (response.status === 422) {
            setFormValidationErrors(response, setError)
          }
          return
        }

        onAssetCreated()
        handleClose()
      } catch (error) {
        console.error('Error creating asset:', error)
      } finally {
        setIsSubmitting(false)
      }
    }
  }

  const handleClose = () => {
    reset({ Name: '', Type: '', Status: '', EmailTypeKey: '' })
    setSelectedFile(null)
    setFileError(undefined)
    setPickedDriveFile(null)
    setDriveError(undefined)
    setPickedOneDriveFile(null)
    setOneDriveError(undefined)
    setPickedDropboxFile(null)
    setDropboxError(undefined)
    setIsSubmitting(false)
    setUploadMethod('file')
    onClose()
  }

  const isFileModeSaveDisabled = uploadMethod === 'file' && (!selectedFile || isFileOverLimit || !hasSettings || !hasAllowedFileTypes)
  const isGoogleDriveModeSaveDisabled = uploadMethod === 'googledrive' && (!pickedDriveFile || isCloudFileTooLarge)
  const isOneDriveModeSaveDisabled = uploadMethod === 'onedrive' && (!pickedOneDriveFile || isCloudFileTooLarge)
  const isDropboxModeSaveDisabled = uploadMethod === 'dropbox' && (!pickedDropboxFile || isCloudFileTooLarge)

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/50 flex items-center justify-center p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-asset-modal-title"
        className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${BRAND_CLASSES.BG.PRIMARY_LIGHT}`}>
              <Package className={`w-5 h-5 ${BRAND_CLASSES.TEXT.PRIMARY}`} />
            </div>
            <div>
              <h2 id="create-asset-modal-title" className="text-xl font-semibold text-slate-900 dark:text-white">
                Create Asset
              </h2>
              <p className="text-sm text-slate-600 dark:text-slate-300">
                Upload a new asset for this web
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            aria-label="Close modal"
            className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form Content */}
        <form onSubmit={handleSubmit(onSubmit)} className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-6">
            {/* Asset Name */}
            <TextBoxField
              control={control}
              name="Name"
              label="Asset Name"
              required
              showCharCounter
              maxLength={NAME_MAX_LENGTH}
              placeholder="Enter asset name"
              autoFocus
              rules={{
                required: 'Asset name is required',
                validate: value => {
                  const trimmed = (value ?? '').trim()
                  if (trimmed.length === 0) return 'Asset name is required'
                  if (trimmed.length > NAME_MAX_LENGTH) return `Asset name must be ${NAME_MAX_LENGTH} characters or less`
                  return true
                }
              }}
            />

            {/* Asset Type */}
            <Select
              label="Type"
              required
              error={errors.Type?.message as string}
              options={typeOptions}
              placeholder="Select asset type"
              {...register('Type', { required: 'Asset type is required' })}
            />

            {/* Status (optional) */}
            {statusOptions.length > 0 && (
              loadingSettings ? (
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Status</label>
                  <div className="h-10 rounded-md border border-slate-300 dark:border-slate-600 overflow-hidden bg-shimmer-light animate-shimmer">
                    <div className="flex items-center justify-between h-full px-3">
                      <div className="h-3 w-32 rounded bg-slate-300/60 dark:bg-slate-500/40" />
                      <div className="h-3 w-3 rounded bg-slate-300/60 dark:bg-slate-500/40" />
                    </div>
                  </div>
                </div>
              ) : (
                <Select
                  label="Status"
                  error={errors.Status?.message as string}
                  options={statusOptions}
                  placeholder="Select status (optional)"
                  {...register('Status')}
                />
              )
            )}

            {/* ORB-363/481: email-type picker for Email Copy. Online Event / Download webs have a
                catalog; Meeting / Demo do not. Each outcome says which it is rather than all three
                collapsing into an absent dropdown. */}
            {showEmailTypeSection && (
              emailTypes.isLoading ? (
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Email type</label>
                  <div className="h-10 rounded-md border border-slate-300 dark:border-slate-600 overflow-hidden bg-shimmer-light animate-shimmer">
                    <div className="flex items-center justify-between h-full px-3">
                      <div className="h-3 w-40 rounded bg-slate-300/60 dark:bg-slate-500/40" />
                      <div className="h-3 w-3 rounded bg-slate-300/60 dark:bg-slate-500/40" />
                    </div>
                  </div>
                </div>
              ) : emailTypes.error ? (
                <div className="flex items-start gap-2.5 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-3">
                  <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
                      Couldn't load the email type catalog.
                    </p>
                    <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                      You can still create a blank Email Copy and write it yourself.{' '}
                      <button
                        type="button"
                        onClick={() => emailTypes.refetch()}
                        className="underline hover:no-underline font-medium"
                      >
                        Try again
                      </button>
                    </p>
                  </div>
                </div>
              ) : hasEmailTypeCatalog ? (
                <Select
                  label="Email type"
                  options={emailTypeOptions}
                  {...register('EmailTypeKey')}
                />
              ) : emailTypeCatalogEmpty ? (
                <div className="flex items-start gap-2.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700/30 p-3">
                  <Info className="w-4 h-4 text-slate-500 dark:text-slate-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-slate-700 dark:text-slate-200">
                      {webType} webs don't have prewritten email types yet
                    </p>
                    <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">
                      That's expected, not a problem — carry on and Webster will draft this copy from
                      the campaign's own context instead of a template.
                    </p>
                  </div>
                </div>
              ) : null
            )}

            {/* Webster type info banner */}
            {isWebsterType && (
              <div className="rounded-lg border border-primary-200 dark:border-primary-800 bg-primary-50 dark:bg-primary-900/20 p-4">
                <p className="text-sm font-medium text-primary-800 dark:text-primary-300">
                  {selectedTypeLabel === 'Email Template'
                    ? 'Webster will help you design the email template after creation.'
                    : selectedTypeLabel === 'Email Copy'
                    ? 'Webster will help you draft the email copy after creation.'
                    : 'Webster will help you build the landing page after creation.'}
                </p>
                <p className="text-xs text-primary-600 dark:text-primary-400 mt-1">
                  No file upload needed — open the asset and use the Webster builder to generate content.
                </p>
              </div>
            )}

            {/* Upload Source Selection (hidden for Webster types) */}
            {!isWebsterType && (<>
            <div className="space-y-3">
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => { setUploadMethod('file'); setPickedDriveFile(null); setDriveError(undefined); setPickedOneDriveFile(null); setOneDriveError(undefined); setPickedDropboxFile(null); setDropboxError(undefined) }}
                  className={`flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                    uploadMethod === 'file'
                      ? 'border-primary-300 dark:border-primary-700 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 shadow-sm'
                      : 'border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700'
                  }`}
                >
                  <Upload className="w-4 h-4" />
                  File Upload
                </button>
                <button
                  type="button"
                  onClick={() => { setUploadMethod('googledrive'); setSelectedFile(null); setFileError(undefined); setPickedOneDriveFile(null); setOneDriveError(undefined); setPickedDropboxFile(null); setDropboxError(undefined) }}
                  className={`flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                    uploadMethod === 'googledrive'
                      ? 'border-primary-300 dark:border-primary-700 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 shadow-sm'
                      : 'border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700'
                  }`}
                >
                  <GoogleDriveIcon className="w-4 h-4" />
                  Google Drive
                </button>
                <button
                  type="button"
                  onClick={() => { setUploadMethod('onedrive'); setSelectedFile(null); setFileError(undefined); setPickedDriveFile(null); setDriveError(undefined); setPickedDropboxFile(null); setDropboxError(undefined) }}
                  className={`flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                    uploadMethod === 'onedrive'
                      ? 'border-primary-300 dark:border-primary-700 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 shadow-sm'
                      : 'border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700'
                  }`}
                >
                  <OneDriveIcon className="w-4 h-4" />
                  OneDrive
                </button>
                <button
                  type="button"
                  onClick={() => { setUploadMethod('dropbox'); setSelectedFile(null); setFileError(undefined); setPickedDriveFile(null); setDriveError(undefined); setPickedOneDriveFile(null); setOneDriveError(undefined) }}
                  className={`flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                    uploadMethod === 'dropbox'
                      ? 'border-primary-300 dark:border-primary-700 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 shadow-sm'
                      : 'border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700'
                  }`}
                >
                  <DropboxIcon className="w-4 h-4" />
                  Dropbox
                </button>
              </div>
            </div>

            {/* File Upload Mode */}
            {uploadMethod === 'file' && (
              <>
                {loadingSettings ? (
                  <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400 py-4">
                    <div className="w-4 h-4 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" />
                    Loading file restrictions...
                  </div>
                ) : !hasSettings ? (
                  <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-4">
                    <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
                      Workflow settings must be configured before uploading assets.
                    </p>
                    <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                      Go to Settings &gt; Asset Workflow to configure file restrictions.
                    </p>
                  </div>
                ) : !hasAllowedFileTypes ? (
                  <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-4">
                    <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
                      No file types are currently allowed.
                    </p>
                    <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                      Enable at least one file category or configure allowed extensions in workflow settings.
                    </p>
                  </div>
                ) : (
                  <>
                    <FileUpload
                      label="File"
                      required
                      selectedFile={selectedFile}
                      onFileChange={(file) => {
                        setSelectedFile(file)
                        if (file) setFileError(undefined)
                      }}
                      onError={setFileError}
                      error={fileError}
                      accept={acceptString}
                      maxSizeMB={maxSizeMB}
                      helperText={`Select a file to upload (max ${maxSizeMB} MB)`}
                    />

                    {/* 100MB hard block */}
                    {isFileOverLimit && (
                      <div className="flex items-start gap-2.5 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-3">
                        <AlertTriangle className="w-4 h-4 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-red-700 dark:text-red-300">
                          Files over {DIRECT_UPLOAD_MAX_MB} MB cannot be uploaded directly. Use a cloud storage provider instead.
                        </p>
                      </div>
                    )}

                    {/* 50MB soft warning */}
                    {isFileLarge && (
                      <div className="flex items-start gap-2.5 rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 p-3">
                        <Info className="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-blue-700 dark:text-blue-300">
                          Large file detected. For faster uploads, consider using a cloud storage provider for files over {LARGE_FILE_WARNING_MB} MB.
                        </p>
                      </div>
                    )}
                  </>
                )}
              </>
            )}

            {/* Google Drive Mode */}
            {uploadMethod === 'googledrive' && (
              <>
                <GoogleDrivePicker
                  auth={googleDriveAuth}
                  onFilePicked={(file) => {
                    setPickedDriveFile(file)
                    setDriveError(undefined)
                  }}
                  onError={setDriveError}
                />
                {driveError && (
                  <p className="text-sm text-red-600 dark:text-red-400">{driveError}</p>
                )}
              </>
            )}

            {/* OneDrive Mode */}
            {uploadMethod === 'onedrive' && (
              <>
                <OneDrivePicker
                  auth={oneDriveAuth}
                  onFilePicked={(file) => {
                    setPickedOneDriveFile(file)
                    setOneDriveError(undefined)
                  }}
                  onError={setOneDriveError}
                />
                {oneDriveError && (
                  <p className="text-sm text-red-600 dark:text-red-400">{oneDriveError}</p>
                )}
              </>
            )}

            {/* Dropbox Mode */}
            {uploadMethod === 'dropbox' && (
              <>
                <DropboxChooser
                  auth={dropboxAuth}
                  onFilePicked={(file) => {
                    setPickedDropboxFile(file)
                    setDropboxError(undefined)
                  }}
                  onError={setDropboxError}
                />
                {dropboxError && (
                  <p className="text-sm text-red-600 dark:text-red-400">{dropboxError}</p>
                )}
              </>
            )}
            {hasPickedCloudFile && (
              <div className="space-y-2">
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  {pickedCloudSizeText
                    ? `Selected file size: ${pickedCloudSizeText}`
                    : 'Selected file size: not reported by this provider'}
                  {cloudMaxSizeText && ` · Maximum: ${cloudMaxSizeText}`}
                </p>
                {isCloudFileTooLarge && (
                  <div className="flex items-start gap-2.5 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-3">
                    <AlertTriangle className="w-4 h-4 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-700 dark:text-red-300">
                      This file is {pickedCloudSizeText}, which is over the {cloudMaxSizeText} maximum. Please choose a smaller file.
                    </p>
                  </div>
                )}
              </div>
            )}

            </>)}
          </div>

          {/* Footer */}
          <FormActions
            isSubmitting={isSubmitting}
            isSaveDisabled={!isValid || isSubmitting || (!isWebsterType && (isFileModeSaveDisabled || isGoogleDriveModeSaveDisabled || isOneDriveModeSaveDisabled || isDropboxModeSaveDisabled))}
            onCancel={handleClose}
            saveText={isSubmitting ? 'Creating...' : 'Create Asset'}
          />
        </form>
      </div>
    </div>
  )
}
